import i18n from "multilingual";
import { FHIRSave, FHIRSearch } from "./http";
import { extractAndParseJsonPayload, extractSoapGenerationError, extractSOAPNoteTitle, extractTemplateName } from "config/helpers/backgroundTaskUtils/backgroundTaskUtils";

const IGNORED_TASK_GROUP = "task_group@dynamic-template.update.execute";

export const GET_NOTIFICATIONS = async () => {
    try {
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);

        const todayStr = today.toISOString().split("T")[0];
        const sevenDaysAgoStr = sevenDaysAgo.toISOString().split("T")[0];

        const basicQuery =
            `Basic?code=http://enliv.com/fhir/coding/basic/code|notification` +
            `&_lastUpdated=ge${sevenDaysAgoStr}` +
            `&_lastUpdated=le${todayStr}` +
            `&_include=Basic:subject&_sort=-_lastUpdated`;

        const basicResponse = await FHIRSearch(basicQuery);

        const { taskIds, basicResources } = extractActiveNotifications(basicResponse);

        if (taskIds.length === 0) {
            return [];
        }

        const taskQuery =
            `Task?_id=${taskIds.join(",")}` +
            `&_include=Task:patient&_include=Task:subject`;

        const taskResponse = await FHIRSearch(taskQuery);

        const { tasks, patients, encounterIds } = extractTasksAndPatients(taskResponse);

        const encounters = await fetchEncounters(encounterIds);

        return buildNotifications(tasks, patients, encounters, basicResources);
    } catch (error) {
        console.error("Error fetching notifications:", error);
        throw error; // Re-throw to allow caller to handle
    }
};

/**
 * Extract active Basic notification resources and task IDs
 */
const extractActiveNotifications = (basicResponse) => {
    const taskIds = [];
    const basicResources = {};

    basicResponse.data.entry?.forEach((entry) => {
        const resource = entry.resource;

        if (resource?.resourceType === "Basic" && resource?.subject?.reference) {
            const statusTag = resource.meta?.tag?.find(
                (tag) => tag.system === "http://enliv.com/fhir/basic/tag/notification/status"
            );

            // Only include active notifications
            if (statusTag?.code !== "inactive") {
                const taskId = resource.subject.reference.replace("Task/", "");

                if (taskId && !taskIds.includes(taskId)) {
                    taskIds.push(taskId);
                    basicResources[taskId] = resource;
                }
            }
        }
    });

    return { taskIds, basicResources };
};

/**
 * Extract Tasks, Patients, and Encounter IDs from Task response
 */
const extractTasksAndPatients = (taskResponse) => {
    const tasks = [];
    const patients = {};
    const encounterIds = [];

    taskResponse.data.entry?.forEach((entry) => {
        const resource = entry.resource;

        if (resource?.resourceType === "Task") {
            tasks.push(resource);

            // Extract encounter ID from focus reference
            if (resource?.focus?.reference) {
                const encounterId = resource.focus.reference.replace("Composition/", "");
                if (encounterId && !encounterIds.includes(encounterId)) {
                    encounterIds.push(encounterId);
                }
            }
        }

        if (resource?.resourceType === "Patient") {
            patients[resource.id] = resource;
        }
    });

    return { tasks, patients, encounterIds };
};

/**
 * Fetch encounter/composition resources
 */
const fetchEncounters = async (encounterIds) => {
    if (encounterIds.length === 0) return {};

    const encounters = {};

    try {
        const encounterQuery = `Composition?_id=${encounterIds.join(",")}`;
        const encounterResponse = await FHIRSearch(encounterQuery);

        encounterResponse.data.entry?.forEach((entry) => {
            if (entry.resource?.resourceType === "Composition") {
                encounters[entry.resource.id] = entry.resource;
            }
        });
    } catch (error) {
        console.error("Error fetching encounters:", error);
        // Continue without encounters rather than failing completely
    }

    return encounters;
};

/**
 * Build notification objects from raw FHIR resources
 */
const buildNotifications = (tasks, patients, encounters, basicResources) => {
    return tasks
        .map((task) => {
            // Parse task data
            let taskData = {};
            try {
                taskData = JSON.parse(task?.output?.[0]?.valueString || "{}");
            } catch (e) {
                console.warn("Failed to parse task output:", e);
                taskData = {};
            }

            // Ignore update tasks
            const hasUpdateTaskGroup = taskData?.taskGroups?.some(
                (group) => group.code === IGNORED_TASK_GROUP
            );
            if (hasUpdateTaskGroup) return null;

            // Extract patient and encounter IDs
            const patientId = extractPatientId(task);
            const encounterId = extractEncounterId(task);

            // Get associated resources
            const patient = patientId ? patients[patientId] : null;
            const encounter = encounterId ? encounters[encounterId] : null;
            const basicResource = basicResources[task.id];

            // Skip if no basic resource found
            if (!basicResource) {
                console.warn("No basic resource found for task:", task.id);
                return null;
            }

            // Extract metadata
            const patientName = formatPatientName(patient);
            const isRead = checkIfRead(basicResource);
            const taskCategory = determineTaskCategory(taskData);
            const title = generateNotificationTitle(task, taskData, taskCategory);
            const error = extractError(task);

            return {
                ...taskData,
                patientName,
                encounterId,
                patientId,
                encounter,
                type: "background_task",
                task_category: taskCategory,
                isRead,
                basicResourceId: basicResource?.id,
                basicResource,
                taskResource: task,
                taskId: task.id,
                addedOn: taskData?.updatedAt,
                lastUpdated: basicResource?.meta?.lastUpdated,
                title,
                error,
            };
        })
        .filter(Boolean);
};

/**
 * Extract patient ID from task
 */
const extractPatientId = (task) => {
    if (task.for?.reference?.startsWith("Patient/")) {
        return task.for.reference.replace("Patient/", "");
    }
    if (task.subject?.reference?.startsWith("Patient/")) {
        return task.subject.reference.replace("Patient/", "");
    }
    return null;
};

/**
 * Extract encounter ID from task
 */
const extractEncounterId = (task) => {
    if (task.focus?.reference) {
        return task.focus.reference.replace("Composition/", "");
    }
    return null;
};

/**
 * Format patient name from Patient resource
 */
const formatPatientName = (patient) => {
    if (!patient?.name?.[0]) return null;

    const given = patient.name[0].given?.join(" ") || "";
    const family = patient.name[0].family || "";
    const fullName = `${given} ${family}`.trim();

    return fullName || null;
};

/**
 * Check if notification has been read
 */
const checkIfRead = (basicResource) => {
    const isReadTag = basicResource?.meta?.tag?.find(
        (tag) => tag.system === "http://enliv.com/fhir/basic/tag/notification/is-read"
    );
    return isReadTag?.code === "true";
};

/**
 * Determine task category (template or soap)
 */
const determineTaskCategory = (taskData) => {
    const isTemplate = taskData?.taskGroups?.some(
        (item) => item.code === "task_group@dynamic-template.generate-preview.execute"
    );
    return isTemplate ? "template" : "soap";
};

/**
 * Generate notification title based on task status and category
 */
const generateNotificationTitle = (task, taskData, taskCategory) => {
    if (taskCategory === "template") {
        const templateName = extractTemplateName(taskData);
        if (task.status === "completed" && templateName) {
            return `${templateName} Created Successfully`;
        }
        if (task.status === "failed") {
            return "Failed to generate template";
        }
    }

    if (taskCategory === "soap") {
        if (task.status === "completed") {
            const soapTitle = extractSOAPNoteTitle(taskData);
            if (soapTitle) return soapTitle;
        }
        if (task.status === "failed") {
            return i18n.t("soap.failed");
        }
    }

    return "";
};

/**
 * Extract error message from task
 */
const extractError = (task) => {
    try {
        const payload = extractAndParseJsonPayload(task);
        return extractSoapGenerationError(payload);
    } catch (e) {
        console.warn("Failed to extract error:", e);
        return null;
    }
};

export const UPDATE_SINGLE_NOTIFICATION_READ_STATUS = async (
    notification,
    isRead,
    currentPractitionerId
) => {
    try {
        const basicResource = notification.basicResource || {};
        const basicResourceId = notification.basicResourceId;

        if (!basicResourceId) {
            throw new Error("Notification missing basicResourceId");
        }

        // Update is-read tag
        const existingTags = basicResource.meta?.tag || [];
        const filteredTags = existingTags.filter(
            (tag) => tag.system !== "http://enliv.com/fhir/basic/tag/notification/is-read"
        );

        const updatedTags = [
            ...filteredTags,
            {
                code: isRead.toString(),
                system: "http://enliv.com/fhir/basic/tag/notification/is-read",
            },
        ];

        const updatedResource = {
            resourceType: "Basic",
            id: basicResourceId,
            code: {
                coding: [
                    {
                        code: "notification",
                        system: "http://enliv.com/fhir/coding/basic/code",
                    },
                ],
            },
            subject: {
                reference: `Task/${notification.taskId}`,
            },
            author: basicResource.author || {
                reference: `Practitioner/${currentPractitionerId}`,
            },
            identifier: basicResource.identifier || [],
            meta: {
                tag: updatedTags,
                extension: basicResource.meta?.extension || [],
                source: currentPractitionerId,
            },
        };

        const response = await FHIRSave("PUT", updatedResource);

        return {
            success: true,
            message: "Successfully updated notification read status",
            data: response.data,
        };
    } catch (error) {
        console.error("Error updating notification read status:", error);
        throw error;
    }
};

export const DELETE_SINGLE_NOTIFICATION = async (notification, currentPractitionerId) => {
    try {
        const basicResource = notification.basicResource || {};
        const taskResource = notification.taskResource || {};
        const basicResourceId = notification.basicResourceId;
        const taskId = notification.taskId;

        if (!basicResourceId || !taskId) {
            throw new Error("Notification missing basicResourceId or taskId");
        }

        // Update Basic resource status to inactive
        const existingTags = basicResource.meta?.tag || [];
        const filteredTags = existingTags.filter(
            (tag) => tag.system !== "http://enliv.com/fhir/basic/tag/notification/status"
        );

        const updatedTags = [
            ...filteredTags,
            {
                code: "inactive",
                system: "http://enliv.com/fhir/basic/tag/notification/status",
            },
        ];

        const updatedBasicResource = {
            resourceType: "Basic",
            id: basicResourceId,
            code: {
                coding: [
                    {
                        code: "notification",
                        system: "http://enliv.com/fhir/coding/basic/code",
                    },
                ],
            },
            subject: {
                reference: `Task/${taskId}`,
            },
            author: basicResource.author || {
                reference: `Practitioner/${currentPractitionerId}`,
            },
            identifier: basicResource.identifier || [],
            meta: {
                tag: updatedTags,
                extension: basicResource.meta?.extension || [],
                source: currentPractitionerId,
            },
        };

        // Update Task resource to cancelled
        const updatedTaskResource = {
            ...taskResource,
            status: "cancelled",
        };

        // Execute both updates in a single transaction
        const bundle = {
            resourceType: "Bundle",
            type: "transaction",
            entry: [
                {
                    request: {
                        method: "PUT",
                        url: `Basic/${basicResourceId}`,
                    },
                    resource: updatedBasicResource,
                },
                {
                    request: {
                        method: "PUT",
                        url: `Task/${taskId}`,
                    },
                    resource: updatedTaskResource,
                },
            ],
        };

        await FHIRSave("executeBundle", bundle);

        return {
            success: true,
            message: "Successfully deleted notification",
        };
    } catch (error) {
        console.error("Error deleting notification:", error);
        throw error;
    }
};

export const BULK_CLEAR_NOTIFICATIONS = async (notifications, currentPractitionerId) => {
    try {
        if (!notifications?.length) {
            return { success: true, message: "No notifications to clear" };
        }

        const bundleEntries = [];

        notifications.forEach((notification) => {
            const basicResource = notification.basicResource || {};
            const taskResource = notification.taskResource || {};
            const basicResourceId = notification.basicResourceId;
            const taskId = notification.taskId;

            if (!basicResourceId || !taskId) {
                console.warn("Skipping notification missing required IDs:", notification);
                return;
            }

            // Update Basic resource status to inactive
            const existingTags = basicResource.meta?.tag || [];
            const filteredTags = existingTags.filter(
                (tag) => tag.system !== "http://enliv.com/fhir/basic/tag/notification/status"
            );

            const updatedTags = [
                ...filteredTags,
                {
                    code: "inactive",
                    system: "http://enliv.com/fhir/basic/tag/notification/status",
                },
            ];

            // Add Basic resource update
            bundleEntries.push({
                request: {
                    method: "PUT",
                    url: `Basic/${basicResourceId}`,
                },
                resource: {
                    resourceType: "Basic",
                    id: basicResourceId,
                    code: {
                        coding: [
                            {
                                code: "notification",
                                system: "http://enliv.com/fhir/coding/basic/code",
                            },
                        ],
                    },
                    subject: {
                        reference: `Task/${taskId}`,
                    },
                    author: basicResource.author || {
                        reference: `Practitioner/${currentPractitionerId}`,
                    },
                    identifier: basicResource.identifier || [],
                    meta: {
                        tag: updatedTags,
                        extension: basicResource.meta?.extension || [],
                        source: currentPractitionerId,
                    },
                },
            });

            // Add Task update
            bundleEntries.push({
                request: {
                    method: "PUT",
                    url: `Task/${taskId}`,
                },
                resource: {
                    ...taskResource,
                    status: "cancelled",
                },
            });
        });

        if (bundleEntries.length === 0) {
            console.warn("No valid entries to clear");
            return { success: false, message: "No valid notifications to clear" };
        }

        // Execute bundle transaction
        const bundle = {
            resourceType: "Bundle",
            type: "transaction",
            entry: bundleEntries,
        };

        const response = await FHIRSave("executeBundle", bundle);

        // Check for partial failures
        const hasFailures = response.data?.entry?.some(
            entry => entry.response?.status?.startsWith("4") || entry.response?.status?.startsWith("5")
        );

        if (hasFailures) {
            console.warn("Some notifications failed to clear:", response.data);
            return {
                success: false,
                message: "Some notifications failed to clear",
                partialSuccess: true,
            };
        }

        return {
            success: true,
            message: `Successfully cleared ${notifications.length} notifications`,
        };
    } catch (error) {
        console.error("Error bulk clearing notifications:", error);
        throw error;
    }
};

export const BULK_UPDATE_NOTIFICATION_READ_STATUS = async (
    notifications,
    isRead,
    currentPractitionerId
) => {
    try {
        if (!notifications?.length) {
            return { success: true, message: "No notifications to update" };
        }

        const bundleEntries = notifications
            .map(({ basicResource, basicResourceId, taskId }) => {
                if (!basicResourceId || !basicResource || !taskId) return null;

                const existingTags = basicResource.meta?.tag || [];
                const filteredTags = existingTags.filter(
                    (tag) => tag.system !== "http://enliv.com/fhir/basic/tag/notification/is-read"
                );

                const updatedTags = [
                    ...filteredTags,
                    {
                        system: "http://enliv.com/fhir/basic/tag/notification/is-read",
                        code: String(isRead),
                    },
                ];

                return {
                    request: {
                        method: "PUT",
                        url: `Basic/${basicResourceId}`,
                    },
                    resource: {
                        ...basicResource,
                        id: basicResourceId,
                        meta: {
                            ...(basicResource.meta || {}),
                            tag: updatedTags,
                            source: currentPractitionerId,
                        },
                    },
                };
            })
            .filter(Boolean);

        if (!bundleEntries.length) {
            return { success: false, message: "No valid notifications to update" };
        }

        const bundle = {
            resourceType: "Bundle",
            type: "transaction",
            entry: bundleEntries,
        };

        const response = await FHIRSave("executeBundle", bundle);

        return {
            success: true,
            message: `Successfully updated ${bundleEntries.length} notifications`,
            data: response.data,
        };
    } catch (error) {
        console.error("Error bulk updating notifications:", error);
        throw error;
    }
};